import { LazyQueryResultTuple, QueryResult } from '@apollo/client/react';
import _ from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import { TargetIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { LabelType } from '@treams/treams-network';
import {
	Container,
	Empty,
	FlexGap,
	Heading3,
	InfiniteScroll,
	PAGE_CONTENT_WIDTH,
	Reorder,
	Select,
	Skeleton,
	Switch,
	tokens,
} from '@treams/treams-ui';

import GoalCard from './GoalCard';
import GoalTree from './GoalTree';

const EmptyState = styled.div`
	display: flex;
	justify-content: center;
	padding-top: ${tokens.gap.page}px;

	> * {
		max-width: none;
	}
`;

function GoalsStack(props: { children: ReactNode }) {
	return (
		<FlexGap flexDirection="column" gap={tokens.gap.list} width="100%">
			{props.children}
		</FlexGap>
	);
}

export type GoalsQuery =
	| graphql.IndividualGoalsQuery
	| graphql.GroupGoalsQuery
	| graphql.WorkspaceGoalsQuery;

function ReorderableGoalsList(props: {
	goals: graphql.Goal[];
	user?: graphql.User;
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
	modal?: boolean;
	small?: boolean;
	compact?: boolean;
}) {
	const [update] = graphql.useReorderGoalMutation();
	const [orders, setOrders] = useState<Record<string, number>>({});
	const goals = _.sortBy(props.goals, (goal) => -(orders[goal.id] ?? goal.order));

	function order(index: number) {
		const goal = goals[index];

		if (!goal) {
			return Date.now();
		}

		return orders[goal.id] ?? goal.order;
	}

	return (
		<Reorder.Container
			onReorder={async (id, index) => {
				const current = goals.findIndex((goal) => goal.id === id);
				const shift = current > index ? -1 : 0;

				const previous = order(index + shift);
				const next = order(index + shift + 1);
				const mean =
					index === goals.length - 1 ? order(index + shift) / 2 : (next - previous) / 2 + previous;

				setOrders({ ...orders, [id]: mean });

				await update({ variables: { input: { goalID: id, order: mean } } });

				setOrders(_.omit(orders, id));
			}}
		>
			{goals?.map((goal, index) => (
				<GoalTree.Reorder
					key={goal.id}
					index={index}
					loading={goal.id in orders}
					goal={goal}
					user={props.user}
					mb={`${tokens.gap.list}px`}
				>
					<GoalCard
						goal={goal}
						user={props.user}
						selected={props.selectedGoal?.id === goal.id}
						onSelect={props.onSelect ? () => props.onSelect?.(goal) : undefined}
						owner={false}
						modal={props.modal}
						small={props.small}
						compact={props.compact}
					/>
				</GoalTree.Reorder>
			))}
		</Reorder.Container>
	);
}

function PlainGoalsList(props: {
	goals: graphql.Goal[];
	user?: graphql.User;
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
	modal?: boolean;
	small?: boolean;
	compact?: boolean;
}) {
	const goals = _.sortBy(props.goals, (goal) => -goal.order);

	return (
		<GoalsStack>
			{goals.map((goal) => (
				<GoalTree key={goal.id} goal={goal} user={props.user} small={props.small || props.compact}>
					<GoalCard
						goal={goal as graphql.Goal}
						user={props.user}
						selected={props.selectedGoal?.id === goal.id}
						onSelect={props.onSelect ? () => props.onSelect?.(goal) : undefined}
						owner={false}
						modal={props.modal}
						small={props.small}
						compact={props.compact}
					/>
				</GoalTree>
			))}
		</GoalsStack>
	);
}

export function GenericGoalsList(props: {
	user?: graphql.User;
	query: QueryResult<GoalsQuery>;
	path: string;
	sortKey?: string;
	reorderable?: boolean;
	empty: { title: string; description: string };
	selectedGoal?: graphql.Goal;
	labelId?: string;
	onSelect?: (goal: graphql.Goal) => void;
	modal?: boolean;
	small?: boolean;
	compact?: boolean;
}) {
	const goals: graphql.Goal[] = props.query.data
		? _.get(props.query.data, props.path).edges.map(({ node }) => node as graphql.Goal)
		: [];
	const sortedGoals = props.sortKey ? _.sortBy(goals, props.sortKey) : goals;

	const filteredGoals = props.labelId
		? sortedGoals.filter((goal) => goal.label?.id === props.labelId)
		: sortedGoals;

	const list = props.reorderable ? (
		<ReorderableGoalsList
			goals={filteredGoals}
			user={props.user}
			selectedGoal={props.selectedGoal}
			onSelect={props.onSelect}
			modal={props.modal}
			small={props.small}
			compact={props.compact}
		/>
	) : (
		<PlainGoalsList
			goals={filteredGoals}
			user={props.user}
			selectedGoal={props.selectedGoal}
			onSelect={props.onSelect}
			modal={props.modal}
			small={props.small}
			compact={props.compact}
		/>
	);

	return props.query.data ? (
		goals.length > 0 ? (
			<GoalsStack>
				{list}
				<InfiniteScroll
					query={props.query}
					path={props.path}
					reverse={!!props.query.variables?.last}
				>
					<GoalsStack>
						<Skeleton.Repeat count={3}>
							<GoalCard.Skeleton small={props.small} compact={props.compact} />
						</Skeleton.Repeat>
					</GoalsStack>
				</InfiniteScroll>
			</GoalsStack>
		) : (
			<Skeleton.Empty
				contents={
					<EmptyState>
						<Empty
							icon={<TargetIcon />}
							title={props.empty.title}
							description={props.empty.description}
						/>
					</EmptyState>
				}
			>
				<GoalsStack>
					<Skeleton.Repeat count={3}>
						<GoalCard.Skeleton small={props.small} compact={props.compact} />
					</Skeleton.Repeat>
				</GoalsStack>
			</Skeleton.Empty>
		)
	) : (
		<GenericGoalsList.Skeleton small={props.small} compact={props.compact} />
	);
}

GenericGoalsList.Skeleton = function GenericGoalsListSkeleton(props: {
	small?: boolean;
	compact?: boolean;
}) {
	return (
		<GoalsStack>
			<Skeleton.Repeat count={3}>
				<GoalCard.Skeleton small={props.small} compact={props.compact} />
			</Skeleton.Repeat>
		</GoalsStack>
	);
};

type GoalLabelOption = { title: string; description?: string; value?: string };

export default function GoalsList(props: {
	user?: graphql.User;
	query: QueryResult<GoalsQuery>;
	path: string;
	moments?: ReactNode;
	header?: ReactNode;
	completedQuery?: QueryResult<GoalsQuery>;
	archivedQuery?: LazyQueryResultTuple<GoalsQuery, object>;
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
	reorderable?: boolean;
	contained?: boolean;
}) {
	const [showArchived, setShowArchived] = useState<boolean>(false);
	const [selectedLabelId, setSelectedLabelId] = useState<string | undefined>();
	const goalLabels = graphql.usePaginatedLabelsQuery({
		variables: { isArchived: false, type: LabelType.Goal },
	});
	const hasGoalLabels = useFeature(graphql.FeatureName.GoalLabels);
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const goalLabelsOptions: GoalLabelOption[] | undefined =
		goalLabels.data?.paginatedLabels.edges.map((edge) => ({
			value: edge.node.id,
			title: localize(edge.node.title).text,
			description: edge.node.description ? localize(edge.node.description).text : '',
		}));
	const allOption: GoalLabelOption = { title: t('goals.list.label.all'), value: undefined };

	useEffect(() => {
		if (!showArchived) {
			return;
		}

		props.archivedQuery?.[0]();
	}, [showArchived]);

	const hasCompletedGoals = props.completedQuery?.data
		? _.get(props.completedQuery.data, props.path).edges.length > 0
		: false;

	const hasGoalLabelOptions = goalLabelsOptions && goalLabelsOptions.length > 0;

	const onLabelChange = (value: string) => {
		const valueToSet = value === selectedLabelId ? undefined : value;
		setSelectedLabelId(valueToSet);
	};

	const contents = (
		<>
			{props.header}

			{props.archivedQuery && (
				<FlexGap
					alignItems={['stretch', 'stretch', 'center']}
					flexDirection={['column', 'column', 'row']}
					justifyContent={['flex-start', 'flex-start', 'space-between']}
					gap={tokens.gap.list}
					mb={`${tokens.gap.page}px`}
				>
					<Switch
						label={t('goals.list.archivedGoals.button')}
						on={showArchived}
						onChange={(on) => setShowArchived(on)}
					/>
					{hasGoalLabels && hasGoalLabelOptions && (
						<Box flexGrow={0} flexShrink={0} ml={[0, 0, 'auto']} width={['100%', '100%', 240]}>
							<Select
								placeholder={t('goals.list.label.placeholder')}
								options={[allOption].concat(goalLabelsOptions)}
								selectedValue={selectedLabelId}
								onChange={onLabelChange}
								search={false}
							/>
						</Box>
					)}
				</FlexGap>
			)}

			{!props.onSelect && props.moments}

			{!showArchived ? (
				<FlexGap flexDirection="column" gap={tokens.gap.section}>
					<Box>
						<Heading3 mb={`${tokens.gap.list}px`}>{t('goals.activeGoals.title')}</Heading3>
						<GenericGoalsList
							labelId={selectedLabelId}
							user={props.user}
							query={props.query}
							path={props.path}
							reorderable={props.reorderable}
							empty={{
								title: t('goals.index.empty.title'),
								description: t('goals.index.empty.description'),
							}}
							selectedGoal={props.selectedGoal}
							onSelect={props.onSelect}
						/>
					</Box>

					{hasCompletedGoals && (
						<Box>
							<Heading3 mb={`${tokens.gap.list}px`}>{t('goals.completedGoals.title')}</Heading3>
							<GenericGoalsList
								labelId={selectedLabelId}
								user={props.user}
								query={props.completedQuery!}
								path={props.path}
								reorderable={props.reorderable}
								empty={{
									title: t('goals.completed.empty.title'),
									description: t('goals.completed.empty.description'),
								}}
								selectedGoal={props.selectedGoal}
								onSelect={props.onSelect}
							/>
						</Box>
					)}
				</FlexGap>
			) : (
				<Box>
					<Heading3 mb={`${tokens.gap.list}px`}>{t('goals.archivedGoals.title')}</Heading3>
					<GenericGoalsList
						labelId={selectedLabelId}
						user={props.user}
						query={props.archivedQuery![1]}
						path={props.path}
						sortKey="archivedAt"
						reorderable={props.reorderable}
						empty={{
							title: t('goals.archive.empty.title'),
							description: t('goals.archive.empty.description'),
						}}
						selectedGoal={props.selectedGoal}
						onSelect={props.onSelect}
					/>
				</Box>
			)}
		</>
	);

	if (props.contained === false) {
		return contents;
	}

	return (
		<Container mt={`${tokens.gap.page}px`} mb={4} wide={PAGE_CONTENT_WIDTH}>
			{contents}
		</Container>
	);
}

GoalsList.Skeleton = function GoalsListSkeleton() {
	return (
		<Box mt={1}>
			<Heading3.Skeleton mb={`${tokens.gap.list}px`} />
			<GenericGoalsList.Skeleton />
		</Box>
	);
};
